import { FC, useState } from 'react';
import NavigationBtnControl from 'components/BookingForm/NavigationBtnControl/NavigationBtnControl';
import styles from '../InsuranceBookingConfirmation.module.scss';

import InsuranceUpload from './InsuranceUpload/InsuranceUpload';
import { useForm } from 'react-hook-form';
import { updateBookingInsurance } from 'services/APIs';
import { loadOtpToken } from 'utils/storage';
import { resetTempAccessToken } from 'redux/authSlice';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import { useHistory } from 'react-router-dom';
import useNotifyBookingCompleted from 'hooks/useNotifyBookingCompleted';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';

interface InsuranceUploadUseForm {
  fileFront: File | null;
  fileBack: File | null;
}

const InsuranceUploadForm: FC = () => {
  const { setValue, watch, handleSubmit } = useForm<InsuranceUploadUseForm>({
    mode: 'all',
    defaultValues: {
      fileFront: null,
      fileBack: null,
    },
  });

  const { fileFront, fileBack } = watch();

  const {
    bookingFormSlice: {
      step1: { phoneNumber },
      step3: { tempAccessToken, bookingId },
    },
    clinicDetailsSlice: {
      clinicData: { isInsuranceRequired },
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const history = useHistory();

  const { onBookingCompleted, isRedirectEnabled } = useNotifyBookingCompleted();

  const [isRedirecting, setIsRedirecting] = useState(false);

  const onSubmit = async (data: InsuranceUploadUseForm) => {
    const { fileFront, fileBack } = data;
    const formData = new FormData();
    formData.append('bookingId', bookingId);

    if (fileFront) {
      formData.append('insuranceFront', fileFront);
    }
    if (fileBack) {
      formData.append('insuranceBack', fileBack);
    }

    try {
      await updateBookingInsurance(formData, {
        otpToken: loadOtpToken(phoneNumber),
        tmpAccessToken: tempAccessToken,
      });

      dispatch(resetTempAccessToken());

      if (isRedirectEnabled) {
        setIsRedirecting(true);
        onBookingCompleted();
      }

      return history.go(0);
    } catch (error) {}
  };

  return (
    <>
      {isRedirecting && <LoadingScreen message="Redirecting..." />}
      <div className={styles['upload-instruction']}>
        <h3>Insurance Card</h3>
        <p>
          Ensure the image is bright, in focus, and fully shows all fields on
          the card so the clinic can verify your benefits accurately.
        </p>
      </div>
      <div className={styles['upload-section']}>
        <div>
          <InsuranceUpload
            id={'insurance-front'}
            file={fileFront}
            setFile={(value) =>
              setValue('fileFront', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
          <span>Front</span>
        </div>

        <div>
          <InsuranceUpload
            id={'insurance-back'}
            file={fileBack}
            setFile={(value) =>
              setValue('fileBack', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
          <span>Back</span>
        </div>
      </div>

      <NavigationBtnControl
        nextBtnTitle="Done"
        onNext={handleSubmit(onSubmit)}
        isDisabledNextBtn={isInsuranceRequired && (!fileFront || !fileBack)}
      />
    </>
  );
};

export default InsuranceUploadForm;
